// Models
import { JobStatus } from './job-status.enum';
import { JobData } from './job-data.model';
import { CurrentJobChannelValues } from './current-job-channel-values';
import { JobOptions } from './job-options.enum';
import { JobStatusViewModel, LocationDto } from '../../../shared/models';
import { CompanyViewModel } from '../../company/models/company-view-model.model';
import { JobCommentReturnDto } from './job-comment-return-dto';
import { UnitPeriodChannelDto } from '../../channel/models/unit-period-channel-dto.model';
import { UnitPeriodChannelOffsetDto } from './unit-period-channel-offset-dto.model';
import { UserUnitPeriodChannelDto } from '../../channel/models/user-unit-period-channel-dto';
import { UnitPeriodDto } from './unit-period-dto.model';
import { DeviceDto } from './device-dto.model';
import { JobScopeDto } from './job-scope-dto.model';
import { JobTypeChannelMappingDto } from './job-type-channel-mapping-dto.model';
import { AssetViewModel } from '../../asset/string/models/string-view.model';
import { JobTypeDto } from '../components/daily-report/models/job-type-dto';
import { JobDailyReportDto } from '../components/daily-report/models/job-daily-report-dto';

// Enums
import { ChangeType } from '../enums/change-type.enum';

export class Job {
	id?: number;
	key?: string;
	start?: Date;
	end?: Date;
	name?: string;
	unit?: string;
	unitDescription?: string;
	unitId?: number;
	location?: LocationDto;
	identifier?: string;
	string?: string;
	companyId: number;
	company: CompanyViewModel;
	wellName?: string;
	wellNumber?: string;
	jobScopeId?: number;
	jobScope?: JobScopeDto;
	description?: string;
	status?: JobStatus;
	data?: Array<JobData>;
	deviceId?: number;
	device: DeviceDto;
	lastDataPoint?: CurrentJobChannelValues;
	options?: JobOptions;
	jobStatus?: JobStatusViewModel;
	jobTypeId?: number;
	jobType: JobTypeDto;
	jobLinksActive?: boolean = false;
	guid: string;
	guidMatched: boolean;
	utcDateTimeOffset: number;
	lastSyncRequest?: Date;
	isBeingDeleted?: boolean;

	// These are really from JobChannels, but leaving for now because of Public Link
	jobCommentReturn: JobCommentReturnDto;
	jobDailyReports: JobDailyReportDto[] = [];
	unitPeriods: UnitPeriodDto[] = [];
	unitPeriodChannels: UnitPeriodChannelDto[] = [];
	unitPeriodChannelOffsets: UnitPeriodChannelOffsetDto[] = [];
	userUnitPeriodChannels: UserUnitPeriodChannelDto[] = [];
	jobTypeChannelMappings: JobTypeChannelMappingDto[] = [];
	bops: AssetViewModel[] = [];

	constructor(t?: Job) {
		if (t) {
			Object.assign(this, t);
		}
	}

	get locationDisplay() {
		let hasLocationName: boolean = !!this.location;
		let hasUnitName: boolean = this.unit && this.unit.length > 0;
		if (hasLocationName && hasUnitName) return `${this.location.name} - ${this.unit} ${this.unitDescription}`;

		if (hasLocationName) return this.location.name;

		if (hasUnitName) return `${this.unit} - ${this.unitDescription}`;

		return '';
	}

	get nameDisplay() {
		let hasClientName: boolean = this.company && this.company.name && this.company.name.length > 0;
		let hasWellName: boolean = this.wellName && this.wellName.length > 0;
		let hasWellNumber: boolean = this.wellNumber && this.wellNumber.length > 0;

		if (hasClientName && hasWellName && hasWellNumber) return `${this.company.name} - ${this.wellName} ${this.wellNumber}`;

		if (hasClientName && hasWellName) return `${this.company.name} - ${this.wellName}`;

		if (hasClientName) return this.company.name;

		if (hasWellName && hasWellNumber) return `${this.wellName} ${this.wellNumber}`;

		if (hasWellName) return this.wellName;

		return '';
	}

	get wellDisplay() {
		let hasWellName: boolean = this.wellName && this.wellName.length > 0;
		let hasWellNumber: boolean = this.wellNumber && this.wellNumber.length > 0;

		if (hasWellName && hasWellNumber) return `${this.wellName} ${this.wellNumber}`;

		if (hasWellName) return `${this.wellName}`;

		return '';
	}
	
	get areLinksDisabled(): boolean {
		return (this.options & JobOptions.LinksDisabled) !== 0;
	}
}

export class JobChange {
	constructor(
		public job: Job,
		public changeType: ChangeType
	) {}
}

export interface JobUpdate {
	job: Job;
	changeType: ChangeType;
	timestamp: Date;
}
